<template>
  <n-config-provider :theme="lightTheme">
    <n-form
      label-placement="top"
      class="discover-control"
    >
      <div
        class="discover-control__ip-address discover-control__ip-address--default"
      >
        <ul>
          <li>
            <a :href="getUrl(fitbikeUrl ?? '')">
              <div>{{ t('connection.discover.continue') }}</div>
            </a>
          </li>
        </ul>
      </div>

      <n-form-item :label="t('connection.discover.info')">
        <n-input-group @keyup.enter="discover">
          <n-input
            v-model:value="macAddress"
            :placeholder="t('connection.discover.macaddress')"
            @keypress="ipAddresses = []"
          />
        </n-input-group>
        <BfButton
          :state="ButtonState.Enabled"
          :type="ButtonType.Outlined"
          style="margin-left: 12px"
          @click="discover"
        >
          {{ t('connection.discover.discover') }}
        </BfButton>
      </n-form-item>

      <div
        v-if="loading"
        class="discover-control__loading"
      >
        <BfLoader />
      </div>

      <div
        v-if="ipAddresses.length && !loading"
        class="discover-control__ip-address"
      >
        <ul>
          <li
            v-for="result in ipAddresses"
            :key="result.InternalIp"
          >
            <a :href="getUrl(result.InternalIp)">
              <div>{{ result.InternalIp }}</div>
              <span>{{ result.MacAddress }}</span>
            </a>
          </li>
        </ul>
      </div>
    </n-form>
  </n-config-provider>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { BfLoader } from '@finetic/bikefitting-ui'
import {
  NConfigProvider,
  NInput,
  NInputGroup,
  NForm,
  NFormItem,
  useMessage,
  lightTheme,
} from 'naive-ui'
import { BfButton, ButtonType, ButtonState } from '@finetic/bikefitting-ui'
import { discoverResult } from '@/types/discover'
import { useI18n } from 'vue-i18n'
import { useConnectionStore } from '@/store/connectionStore'
import { buildUrl } from '@/utils/url'
import { getConfig } from '@/config'

const connectionStore = useConnectionStore()
const message = useMessage()
const router = useRouter()
const { t } = useI18n()

const bikeNo = ref(router.currentRoute.value.query.bikeNo)
const customerId = ref(router.currentRoute.value.query.customerId)
const sessionId = ref(router.currentRoute.value.query.sessionId)

const { discoverIPAddress } = connectionStore

const fitbikeUrl = ref(getConfig('VUE_APP_FITBIKE_URL'))
const loading = ref(false)
const macAddress = ref('')
const ipAddresses = ref<discoverResult[]>([])

// Remove the local buildUrl function and use the imported one
const getUrl = (baseUrl: string) =>
  buildUrl(baseUrl, {
    bikeNo: bikeNo.value as string,
    customerId: customerId.value as string,
    sessionId: sessionId.value as string,
  })

const discover = () => {
  loading.value = true

  discoverIPAddress(macAddress.value)
    .then((data: discoverResult[]) => {
      if (data && data.length > 0) {
        ipAddresses.value = data
        message.success(t('connection.discover.found'))
      } else {
        ipAddresses.value = []
        message.warning(t('connection.discover.noresult'))
      }
    })
    .finally(() => {
      loading.value = false
    })
}
</script>

<style lang="scss" scoped>
.discover-control {
  &__loading {
    margin: 48px;
  }

  &__ip-address {
    &--default {
      margin: 24px 0;
      border-bottom: solid 1px var(--color-dark-20);
      padding-bottom: 24px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        a {
          display: block;
          background-color: var(--color-primary);
          color: var(--color-light);
          font-size: 2.4rem;
          font-weight: 600;
          border-radius: 12px;
          text-align: center;
          padding: 8px;
          margin-top: 24px;
          color: var(--color-light);
          text-decoration: none;
          transition: background-color 0.3s ease;

          &:hover {
            background-color: var(--color-primary-200);
          }

          span {
            font-weight: normal;
            font-size: 1.6rem;
          }
        }
      }
    }
  }
}
</style>
